import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Button, Alert } from "react-bootstrap";
import "ol/ol.css";
import { Map, View } from "ol";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import { fromLonLat } from "ol/proj";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { Vector as VectorLayer } from "ol/layer";
import { Vector as VectorSource } from "ol/source";
import { Style, Icon } from "ol/style";
import { pavilions } from "../utils/pavilionCoordinates";
import getCode from "../utils/utilFunction";

const MapPage = () => {
  const {PavCode } = useParams();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [pavilionNotFound, setPavilionNotFound] = useState(false);

  const PavCodeFinal = PavCode ? getCode(PavCode) : "";

  useEffect(() => {
    if (!mapRef.current) return;
    const pavilion =
      pavilions.find((pav) => pav.id === PavCodeFinal) ||
      pavilions.find((pav) => pav.id === PavCodeFinal.replace(/ΙΣ$|ΟΡ$/, ""));

    if (!pavilion) {
      console.warn(`Pavilion ${PavCodeFinal} (${PavCode}) not found in coordinates`);
      setPavilionNotFound(true);
      return;
    }

    setPavilionNotFound(false);

    const centerCoordinates = calculateCenter(pavilion.coordinates);
    const centerLonLat = fromLonLat(centerCoordinates);

    const markerFeature = new Feature({
      geometry: new Point(centerLonLat),
    });

    const markerStyle = new Style({
      image: new Icon({
        anchor: [0.5, 1],
        src: "https://openlayers.org/en/latest/examples/data/icon.png",
        scale: 1,
      }),
    });

    markerFeature.setStyle(markerStyle);

    const vectorSource = new VectorSource({
      features: [markerFeature],
    });

    const vectorLayer = new VectorLayer({
      source: vectorSource,
    });

    const map = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
        vectorLayer,
      ],
      view: new View({
        center: centerLonLat,
        zoom: 17,
      }),
    });

    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setTarget(null);
        mapInstanceRef.current = null;
      }
    };
  }, [PavCodeFinal]);

  const handleBackToStand = () => {
    navigate(-1);
  };

  if (pavilionNotFound) {
    return (
      <Container className="mt-4">
        <div className="bg-light p-3 d-flex justify-content-between align-items-center mb-3">
          <div>
            <h3 className="mb-0">
              Map Location - Pavilion: {PavCode}
            </h3>
          </div>
          <Button variant="primary" onClick={handleBackToStand}>
            Back to Stand
          </Button>
        </div>
        <Alert variant="danger">
          <Alert.Heading>Pavilion Not Found</Alert.Heading>
          <p>
            The pavilion <strong>{PavCode}</strong> does not have coordinates configured in the system.
          </p>
          <p className="mb-0">
            Please contact support or go back to the stand page.
          </p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-0">
      <div className="bg-light p-3 d-flex justify-content-between align-items-center">
        <div>
          <h3 className="mb-0">
            Map Location - Pavilion: {PavCode}
          </h3>
        </div>
        <Button variant="primary" onClick={handleBackToStand}>
          Back to Stand
        </Button>
      </div>
      <div
        ref={mapRef}
        style={{
          width: "100%",
          height: "calc(100vh - 80px)",
        }}
      />
    </Container>
  );
};

/**
 * Calculate the geographic center of a polygon
 * @param {Array<Array<number>>} coordinates - Array of [longitude, latitude] pairs
 * @returns {Array<number>} - Center as [longitude, latitude]
 */
const calculateCenter = (coordinates) => {
  if (!coordinates || coordinates.length === 0) {
    return [0, 0];
  }

  const sum = coordinates.reduce(
    (acc, coord) => {
      acc[0] += coord[0]; // longitude
      acc[1] += coord[1]; // latitude
      return acc;
    },
    [0, 0]
  );

  return [sum[0] / coordinates.length, sum[1] / coordinates.length];
};

export default MapPage;